import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import {
	EncumbranceTypeLabels,
	getEncumbranceInstitutionName,
} from '@core/util/encumbrance';
import {
	MortgagePriorityDescription,
	getMortgageInvestorNamesOrLenderNames,
} from '@core/util/mortgage';
import {
	EncumbranceActionType,
	LtvQualifyingMortgage,
	isEncumbrance,
} from '@models/encumbrance';
import {
	BusinessMatterClientWithAssociations,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TestHooks } from './mortgage-summary-tile.hooks';

@Component({
	selector: 'app-mortgage-summary-tile',
	templateUrl: './mortgage-summary-tile.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MortgageSummaryTileComponent implements OnInit {
	testHooks = TestHooks;
	private readonly mortgage$ = new BehaviorSubject<LtvQualifyingMortgage>(null);

	@Select(MatterClients.get_investors) private readonly investors$!: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	label$!: Observable<string>;

	ENCUMBRANCE_ACTION_TYPE = EncumbranceActionType;

	@Input() set mortgage(mortgage: LtvQualifyingMortgage) {
		const { mortgage$ } = this;
		if (mortgage !== mortgage$.getValue()) {
			mortgage$.next(mortgage);
		}
	}

	get mortgage() {
		return this.mortgage$.getValue();
	}

	ngOnInit(): void {
		// when the mortgage changes, pull the name (if its an encumbrance)
		// otherwise, listen to the investors and pull the names from there
		this.label$ = this.mortgage$.pipe(
			switchMap((mortgage) =>
				(isEncumbrance(mortgage)
					? of(getEncumbranceInstitutionName(mortgage))
					: this.investors$.pipe(
							map((investors) =>
								getMortgageInvestorNamesOrLenderNames(mortgage, investors),
							),
						)
				).pipe(
					map((names) => {
						const priorty = MortgagePriorityDescription[mortgage.priority];
						const mortgageType = EncumbranceTypeLabels[mortgage.type];

						return priorty
							? `${priorty} mortgage to ${names}`
							: `${mortgageType} to ${names}`;
					}),
				),
			),
		);
	}

	get amount(): number {
		const { mortgage } = this;
		return isEncumbrance(mortgage)
			? mortgage.outstandingAmount
			: mortgage.amount;
	}

	get isRemain(): boolean {
		const { mortgage } = this;
		return (
			isEncumbrance(mortgage) &&
			mortgage.action === EncumbranceActionType.REMAIN
		);
	}

	get pillLabel(): string {
		return this.isRemain ? 'Remain' : 'New';
	}
}
